import { telanganaDistrictKeyFromStored } from "../data/telanganaDistricts";
import type { Application, GeoZone, StoreYard, StoreYardScopeType } from "../types";
import { applicationMatchesGeoZone } from "./geoZones";
import { normalizeGeoLabel } from "./geoLabel";

const SCOPE_RANK: Record<StoreYardScopeType, number> = {
  village: 4,
  mandal: 3,
  district: 2,
  zone: 1,
};

/** True if this beneficiary location is covered by the store’s geographic rule (ignoring other stores). */
export function applicationMatchesStoreYard(app: Application, store: StoreYard, zones: GeoZone[]): boolean {
  switch (store.scopeType) {
    case "district": {
      const dk = telanganaDistrictKeyFromStored(app.district);
      return dk != null && store.districtKeys.length > 0 && store.districtKeys.includes(dk);
    }
    case "mandal": {
      const m = normalizeGeoLabel(app.mandal);
      const appDk = telanganaDistrictKeyFromStored(app.district);
      return store.mandalRefs.some(
        (r) => telanganaDistrictKeyFromStored(r.district) === appDk && normalizeGeoLabel(r.mandalName) === m,
      );
    }
    case "village": {
      const m = normalizeGeoLabel(app.mandal);
      const v = normalizeGeoLabel(app.village);
      const appDk = telanganaDistrictKeyFromStored(app.district);
      return store.villageRefs.some(
        (r) =>
          telanganaDistrictKeyFromStored(r.district) === appDk &&
          normalizeGeoLabel(r.mandalName) === m &&
          normalizeGeoLabel(r.villageName) === v,
      );
    }
    case "zone":
      return store.zoneIds.some((zid) => {
        const z = zones.find((x) => x.id === zid);
        return z ? applicationMatchesGeoZone(app, z) : false;
      });
    default:
      return false;
  }
}

/**
 * Pick the serving store for a beneficiary: prefers the most specific scope (village → mandal → district → zone),
 * then the lowest `sortOrder` among ties.
 */
export function resolveStoreYardForApplication(
  app: Application,
  stores: StoreYard[],
  zones: GeoZone[],
): StoreYard | null {
  const matches = stores.filter((s) => applicationMatchesStoreYard(app, s, zones));
  if (matches.length === 0) return null;
  matches.sort((a, b) => {
    const dr = SCOPE_RANK[b.scopeType] - SCOPE_RANK[a.scopeType];
    if (dr !== 0) return dr;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.id.localeCompare(b.id);
  });
  return matches[0] ?? null;
}
